import './EditFire.scss';
import React, { useEffect, useState, useRef } from "react";
import axios from 'axios';
import Spinner from '../../../Spinner.js';
import locationIcon from '../../../../assets/icons/location-icon.png';
import { SearchBox } from '@mapbox/search-js-react';
import mapboxgl from 'mapbox-gl';
import AddPerimeter from '../AddFire/AddPerimeter.js';
import AddFireForm from '../AddFire/AddFireForm.js';
import { useUserContext } from '../../../../contexts/UserContext.js';
import EditFireForm from './EditFireForm.js';
import cursorManager from '../../../../utils/cursorManager.js';

export default function EditFire(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        panel,
        setPanel,
        map, 
        draw
    } = props;

    const fire = panel.content ? panel.content : {};

    const locationRef = useRef(null);
    const [error, setError] = useState(null);
    const [stage, setStage] = useState('add-fire');
    const [perimeter, setPerimeter] = useState(null);
    const [locationLoading, setLocationLoading] = useState(false);

    const [editFireForm, setEditFireForm] = useState({
        fireName: fire.name,
        status: fire.active,
        cause: fire.cause,
        percontained: fire.perContained,
        type: fire.type,
    });

    let isDragging = false; // Track drag state
    let selectedFeature = null; // Store the feature being dragged
    let eventListeners = []; // Track event listeners for cleanup

    const enableDragForSpecificFire = (fireId) => {
        if (map.current) {
            // Store references to event handlers for cleanup
            const handleMouseDown = (e) => {
                if (!e.features || !e.features.length) return;

                const feature = e.features[0];
                if (feature.fire_id === fireId) { // Check if this is the fire to be dragged
                    isDragging = true;
                    selectedFeature = feature;

                    cursorManager.setCursor('edit-fire-drag', 'grabbing', map);
                }
            };

            const handleMouseMove = (e) => {
                if (!isDragging || !selectedFeature) return;

                const newCoordinates = [e.lngLat.lng, e.lngLat.lat];

                // Update the source dynamically
                const source = map.getSource('Active Fire Source');
                const data = source._data; // Access the GeoJSON data
                const updatedData = {
                ...data,
                features: data.features.map((feature) =>
                    feature.fire_id === fireId
                    ? { ...feature, geometry: { ...feature.geometry, coordinates: newCoordinates } }
                    : feature
                ),
                };

                source.setData(updatedData);
            };

            const handleMouseUp = () => {
                if (isDragging) {
                isDragging = false;
                selectedFeature = null;

                cursorManager.resetCursor('edit-fire-drag', map);
                console.log(`Fire ${fireId} moved to new coordinates.`);
                }
            };

            // Add event listeners and store them for cleanup
            map.current.on('mousedown', 'Active Fire Layer', handleMouseDown);
            map.current.on('mousemove', handleMouseMove);
            map.current.on('mouseup', handleMouseUp);

            // Store event listener info for cleanup
            eventListeners.push(
                { event: 'mousedown', layer: 'Active Fire Layer', handler: handleMouseDown },
                { event: 'mousemove', handler: handleMouseMove },
                { event: 'mouseup', handler: handleMouseUp }
            );
        }
    };

    const cleanupEventListeners = () => {
        if (map.current && eventListeners.length > 0) {
            eventListeners.forEach(({ event, layer, handler }) => {
                if (layer) {
                    map.current.off(event, layer, handler);
                } else {
                    map.current.off(event, handler);
                }
            });
            eventListeners = [];
        }
        // Reset cursor and drag state using cursor manager
        cursorManager.resetCursor('edit-fire-drag', map);
        isDragging = false;
        selectedFeature = null;
    };
    
    useEffect(() => {
        enableDragForSpecificFire(fire.fire_id);

        return () => {
            cleanupEventListeners();
            if (locationRef.current) {
                locationRef.current.remove();
            }
        }
    }, []);
    
    const getLocation = () => {
        setError(false);
        setLocationLoading(true);
        if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const location = [position.coords.longitude, position.coords.latitude];
                 if (location) {
                    map.current.flyTo({ center: location, pitch: 0, zoom: 18 });

                    if (is3D) {
                        setIs3D(false);
                    }

                    if (locationRef.current) {
                        locationRef.current.remove();
                    }

                    const point = document.createElement('div');
                    point.className = 'map-current-location-marker';
                    const marker = new mapboxgl.Marker(point).setLngLat(location).addTo(map.current);
                    locationRef.current = marker;
                    setLocationLoading(false);
                }
            },
            (err) => {
                setLocationLoading(false);
                setError('Trouble getting your location. Please try again.');
            }
        );
        } else {
            setLocationLoading(false);
            setError('Trouble getting your location. Please try again.');
        }
    };

    const stageSwitch = (stage) => {
        switch (stage) {
            case 'add-fire':
                return (
                    <div className='edit-fire-content-instructions'>
                        <div className='edit-fire-content-instructions-instruction'>
                            <div className='edit-fire-content-instructions-instruction-title'>Edit location of fire by choosing a new point on map</div>
                            <div className='edit-fire-content-instructions-instruction-sub-title'>Option 1: Current Location</div>
                            {locationLoading ?
                            <div style={{ paddingTop: '1rem' }}>
                                <Spinner />
                            </div>
                            :
                            <div 
                                className='edit-fire-content-instructions-instruction-current-location-btn'
                                onClick={getLocation}
                            >
                                <img src={locationIcon} className='edit-fire-content-instructions-instruction-current-location-btn-icon'/>
                                <div 
                                    className='edit-fire-content-instructions-instruction-current-location-btn-text'
                                >Use Current Location
                                </div>
                            </div>}
                            <div className='edit-fire-content-instructions-instruction-sub-title'>Option 2: Search a location</div>
                            <SearchBox
                                accessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN} 
                                value=''
                                onRetrieve={(e) => {
                                    const location = e.features[0].geometry.coordinates;
                                    map.current.flyTo({center: location, zoom: 10, pitch: 0, offset: [250, 0]});

                                    if (is3D) {
                                        setIs3D(false);
                                    }
                                }}
                            />
                        </div>
                        <div>
                            {fire && 
                            <div
                                className='edit-fire-content-instructions-instruction-sub-title'
                                style={{ margin: 0 }}
                                >{`Location selected: ${fire.latitude.toFixed(6)}, ${fire.longitude.toFixed(6)}`}</div>
                            }
                            <button
                                className={fire ? 'edit-fire-content-instructions-instruction-next-btn' : 'edit-fire-content-instructions-instruction-disabled-btn'}
                                disabled={!fire}
                                onClick={() => {
                                    setStage('add-perimeter');
                                }}
                            >Next
                            </button>
                        </div>
                    </div>
                )
            case 'add-perimeter':
                return (
                    <AddPerimeter 
                        stage={stage}
                        setStage={setStage} 
                        map={map}
                        draw={draw}
                        perimeter={perimeter}
                        setPerimeter={setPerimeter}
                        setPanel={setPanel}
                    />
                )
            case 'fire-form':
                return (
                   <EditFireForm
                        panel={panel}
                        setStage={setStage} 
                        map={map}
                        draw={draw}
                        perimeter={perimeter}
                        setPerimeter={setPerimeter}
                        setPanel={setPanel}
                        editFireForm={editFireForm}
                        setEditFireForm={setEditFireForm}
                        fire={fire}
                    />
                )
        }
    }
    
    return (
        <div className='add-fire'>
            <div className='edit-fire-title'>
            <div className='edit-fire-title-text'>Edit Fire</div>
            </div>
            <div className='edit-fire-description'>Input a fire to the map by specifying its location and details, enabling real-time tracking and monitoring.</div>
            <div className='edit-fire-content'>
                {stageSwitch(stage)}
            </div>
        </div>
    );
}